import { AnimatedSprite, Container, type Texture } from 'pixi.js';
import { getFrames } from '@/assets/resolver';
import { FX, effectsProfile } from '@/config/fx';

/**
 * 'player' = caused by the player (AoE impacts, kills) → cool cyan/white;
 * 'enemy' = boss/ship destruction → the warm palette (docs/12 E4).
 */
export type BlastKind = 'player' | 'enemy';

const BLAST_KEY: Record<BlastKind, string> = {
  player: 'fx/explosion-player',
  enemy: 'fx/explosion-m',
};
const HURT_KEY = 'fx/hurt-ring';

interface Slot {
  active: boolean;
  key: string;
  spr: AnimatedSprite;
}

/**
 * One-shot effects (explosions, hurt ring) from a fixed sprite pool — no
 * allocations mid-fight. The effects-intensity setting scales blast size and
 * caps how many blasts may be alive at once (extra requests are dropped).
 */
export class FxSystem {
  readonly view = new Container();
  private readonly slots: Slot[] = [];
  private readonly frames = new Map<string, Texture[]>();
  private live = 0;
  /** Bumped by clear() so pending death-chain steps die with the arena */
  private generation = 0;

  constructor() {
    // Pre-render every frame set now: no canvas work (or bad key) mid-fight
    this.framesOf(BLAST_KEY.enemy);
    this.framesOf(HURT_KEY);
    const initial = this.framesOf(BLAST_KEY.player);
    for (let i = 0; i < FX.explosions.poolSize; i++) {
      const spr = new AnimatedSprite(initial);
      spr.anchor.set(0.5);
      spr.loop = false;
      spr.visible = false;
      const slot: Slot = { active: false, key: BLAST_KEY.player, spr };
      spr.onComplete = () => this.release(slot);
      this.view.addChild(spr);
      this.slots.push(slot);
    }
  }

  explosion(x: number, y: number, scale = 1, kind: BlastKind = 'player'): void {
    const p = effectsProfile();
    if (this.live >= p.maxBlasts) return;
    this.play(BLAST_KEY[kind], x, y, scale * p.blastScale, FX.explosions.animationSpeed);
  }

  /** Ship took a hit: thin expanding ring — never hides nearby bullets. */
  hurt(x: number, y: number): void {
    this.play(HURT_KEY, x, y, effectsProfile().hurtScale, FX.explosions.hurtAnimationSpeed);
  }

  /** Boss/ship death: delayed chain explosion (length per effects level). */
  bigExplosion(x: number, y: number): void {
    const p = effectsProfile();
    const gen = this.generation;
    const steps = FX.explosions.chain.slice(0, p.chainSteps);
    for (const s of steps) {
      const fire = (): void => {
        if (gen !== this.generation) return;
        this.play(BLAST_KEY.enemy, x + s.dx, y + s.dy, s.scale * p.blastScale,
          FX.explosions.animationSpeed);
      };
      if (s.delay === 0) fire();
      else setTimeout(fire, s.delay);
    }
  }

  clear(): void {
    this.generation++;
    for (const slot of this.slots) {
      if (slot.active) slot.spr.stop();
      this.release(slot);
    }
  }

  private play(key: string, x: number, y: number, scale: number, speed: number): void {
    let slot: Slot | null = null;
    for (const s of this.slots) {
      if (!s.active) {
        slot = s;
        break;
      }
    }
    if (!slot) return;
    const { spr } = slot;
    if (slot.key !== key) {
      spr.textures = this.framesOf(key);
      slot.key = key;
    }
    slot.active = true;
    this.live++;
    spr.position.set(x, y);
    spr.scale.set(scale);
    spr.animationSpeed = speed;
    spr.visible = true;
    spr.gotoAndPlay(0);
  }

  private release(slot: Slot): void {
    if (!slot.active) return;
    slot.active = false;
    slot.spr.visible = false;
    this.live--;
  }

  private framesOf(key: string): Texture[] {
    let f = this.frames.get(key);
    if (!f) {
      f = getFrames(key);
      this.frames.set(key, f);
    }
    return f;
  }
}
